import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Chip, Container, LinearProgress, Stack, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import emailFill from '@iconify/icons-eva/email-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import Page from '../Page';
import PageHeader from '../Outreach/shared/PageHeader';
import StatusChip from '../Outreach/shared/StatusChip';
import { getEnrollment, listExecutions } from '../../utils/outreachApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const CompanyTimeline = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [executions, setExecutions] = useState(null);

  useEffect(() => {
    getEnrollment(id).then(({ data }) => {
      setEnrollment(data);
      listExecutions({ enrollment_id: data.id }).then(({ data: ex }) => setExecutions(ex));
    });
  }, [id]);

  if (!enrollment || !executions) return (
    <Page title="Company Timeline | CRM">
      <Container sx={{ py: 4 }}><LinearProgress /></Container>
    </Page>
  );

  return (
    <Page title={`${enrollment.company_name} | CRM`}>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <PageHeader
          title={enrollment.company_name}
          subtitle={`${enrollment.industry} · ${enrollment.country} · ${enrollment.fte} employees`}
          actions={
            <Button startIcon={<Icon icon={arrowBackFill} />} onClick={() => navigate('/my-outreach')}>
              Back
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Primary contact</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {enrollment.primary_member.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {enrollment.primary_member.email}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}><StatusChip status={enrollment.status} /></Box>
            </Box>
          </Stack>
        </Card>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Sequence timeline</Typography>
        <Stack spacing={1.5}>
          {executions.sort((a, b) => a.step_order - b.step_order).map((e) => (
            <Card key={e.id} variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%',
                             bgcolor: 'primary.lighter', color: 'primary.dark',
                             display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    {e.step_order}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon icon={e.channel?.startsWith('LINKEDIN') ? linkedinFill : emailFill} width={16}
                            color={e.channel?.startsWith('LINKEDIN') ? '#0A66C2' : undefined} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Step {e.step_order} · {e.channel.replace('_', ' ').toLowerCase()}
                      </Typography>
                      <StatusChip status={e.status} />
                    </Stack>
                    {e.rendered_subject && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Subject: {e.rendered_subject}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      {e.scheduled_at && <Typography variant="caption" color="text.secondary">scheduled: {fmt(e.scheduled_at)}</Typography>}
                      {e.sent_at &&      <Typography variant="caption" color="text.secondary">sent: {fmt(e.sent_at)}</Typography>}
                      {e.first_opened_at && <Typography variant="caption" color="text.secondary">opened: {fmt(e.first_opened_at)}</Typography>}
                      {e.reply_received_at && <Typography variant="caption" color="warning.dark">replied: {fmt(e.reply_received_at)}</Typography>}
                    </Stack>
                    {e.error_message && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {e.error_message}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>
    </Page>
  );
};

export default CompanyTimeline;
